import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Trash2, Pencil, Plus, LogOut, Loader2, X, Upload, Briefcase, Tags, FileDown, FileText, Settings, ShoppingBag, Users, Languages, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useShop, API } from "../context/ShopContext";
import { PageTitle } from "../components/Shared";
import ProductFilesAdmin from "../components/ProductFilesAdmin";
import ProductsAdmin from "../components/ProductsAdmin";
import LegalPagesAdmin from "../components/LegalPagesAdmin";
import SettingsAdmin from "../components/SettingsAdmin";
import OrdersAdmin from "../components/OrdersAdmin";
import CustomersAdmin from "../components/CustomersAdmin";
import LicensesAdmin from "./LicensesAdmin";
import Seo from "../components/Seo";
import { imgSrc } from "./Portfolio";

const emptyForm = {
  title_it: "", title_en: "", category: "siti-vetrina", image: "", description_it: "", description_en: "",
  tipologia: "", tipologia_en: "", data: "", data_en: "", seo: "", seo_en: "",
  cliente: "", sede: "", sede_en: "", url: "",
  sec_design: "", sec_design_en: "", sec_plugins: "", sec_plugins_en: "",
  sec_seo: "", sec_seo_en: "", sec_integr: "", sec_integr_en: "",
};

const SECTION_DEFS = [
  { key: "sec_design", keyEn: "sec_design_en", title: "DESIGN GRAFICO", match: /DESIGN/i },
  { key: "sec_plugins", keyEn: "sec_plugins_en", title: "PLUGINS", match: /PLUGIN/i },
  { key: "sec_seo", keyEn: "sec_seo_en", title: "SEO - RICERCA", match: /SEO/i },
  { key: "sec_integr", keyEn: "sec_integr_en", title: "INTEGRAZIONI", match: /INTEGRA/i },
];

const ADMIN_TABS = [
  { key: "ordini", label: "Ordini", icon: ShoppingBag },
  { key: "clienti", label: "Clienti", icon: Users },
  { key: "portfolio", label: "Portfolio", icon: Briefcase },
  { key: "prodotti", label: "Prodotti", icon: Tags },
  { key: "file", label: "File digitali", icon: FileDown },
  { key: "licenze", label: "Licenze", icon: KeyRound },
  { key: "pagine", label: "Pagine legali", icon: FileText },
  { key: "impostazioni", label: "Impostazioni", icon: Settings },
];

const AdminPage = () => {
  const { lang } = useShop();
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("idm_admin_key") || "");
  const [keyInput, setKeyInput] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("ordini");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/portfolio/categories`),
      ]);
      setItems(itemsRes.data);
      setCats(catsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminKey) loadData();
  }, [adminKey, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { key: keyInput });
      localStorage.setItem("idm_admin_key", keyInput);
      setAdminKey(keyInput);
      toast.success("Accesso effettuato");
    } catch {
      toast.error("Chiave non valida");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("idm_admin_key");
    setAdminKey("");
    setKeyInput("");
  };

  const startEdit = (item) => {
    setEditingId(String(item.id));
    const meta = item.meta || {};
    const sections = item.sections || [];
    const sectionFields = {};
    SECTION_DEFS.forEach((def) => {
      const sec = sections.find((s) => def.match.test(s.title));
      sectionFields[def.key] = sec ? sec.lines.join("\n") : "";
      sectionFields[def.keyEn] = sec && sec.lines_en ? sec.lines_en.join("\n") : "";
    });
    setForm({
      title_it: item.title.it,
      title_en: item.title.en,
      category: item.category,
      image: item.image || "",
      description_it: item.description?.it || "",
      description_en: item.description?.en || "",
      tipologia: meta.tipologia || "",
      tipologia_en: meta.tipologia_en || "",
      data: meta.data || "",
      data_en: meta.data_en || "",
      seo: meta.seo || "",
      seo_en: meta.seo_en || "",
      cliente: meta.cliente || "",
      sede: meta.sede || "",
      sede_en: meta.sede_en || "",
      url: meta.url || "",
      ...sectionFields,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAutoTranslate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/portfolio/translate`, {
        title_it: form.title_it,
        description_it: form.description_it,
        tipologia: form.tipologia,
        data: form.data,
        seo: form.seo,
        sede: form.sede,
        sec_design: form.sec_design,
        sec_plugins: form.sec_plugins,
        sec_seo: form.sec_seo,
        sec_integr: form.sec_integr,
      }, authHeaders());
      const t = res.data;
      setForm((f) => ({
        ...f,
        title_en: t.title || f.title_en,
        description_en: t.description || f.description_en,
        tipologia_en: t.tipologia || f.tipologia_en,
        data_en: t.data || f.data_en,
        seo_en: t.seo || f.seo_en,
        sede_en: t.sede || f.sede_en,
        sec_design_en: t.sec_design || f.sec_design_en,
        sec_plugins_en: t.sec_plugins || f.sec_plugins_en,
        sec_seo_en: t.sec_seo || f.sec_seo_en,
        sec_integr_en: t.sec_integr || f.sec_integr_en,
      }));
      toast.success("Traduzione automatica completata");
    } catch {
      toast.error("Errore durante la traduzione automatica");
    } finally {
      setTranslating(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Immagine caricata");
    } catch {
      toast.error("Errore caricamento immagine");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title_it.trim()) {
      toast.error("Il titolo (IT) \u00e8 obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sections: SECTION_DEFS.map((def) =>
          form[def.key].trim()
            ? {
                title: def.title,
                lines: form[def.key].split("\n").map((l) => l.trim()).filter(Boolean),
                lines_en: form[def.keyEn].split("\n").map((l) => l.trim()).filter(Boolean),
              }
            : null
        ).filter(Boolean),
      };
      if (editingId) {
        await axios.put(`${API}/admin/portfolio/${editingId}`, payload, authHeaders());
        toast.success("Lavoro aggiornato");
      } else {
        await axios.post(`${API}/admin/portfolio`, payload, authHeaders());
        toast.success("Lavoro aggiunto");
      }
      cancelEdit();
      loadData();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessione scaduta, accedi di nuovo");
        handleLogout();
      } else {
        toast.error("Errore durante il salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare "${item.title.it}"?`)) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${item.id}`, authHeaders());
      toast.success("Lavoro eliminato");
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  if (!adminKey) {
    return (
      <div data-testid="admin-login-page">
        <Seo title="Admin" />
        <PageTitle title="Area riservata" />
        <div className="idm-container idm-admin-login">
          <form onSubmit={handleLogin} className="idm-admin-login-box">
            <h3>Accesso amministratore</h3>
            <div className="idm-field">
              <label htmlFor="adminkey">Chiave di accesso</label>
              <input
                id="adminkey"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                data-testid="admin-key-input"
              />
            </div>
            <button type="submit" className="idm-btn-yellow idm-btn-block" disabled={loggingIn} data-testid="admin-login-btn">
              {loggingIn ? <Loader2 size={16} className="idm-spin" /> : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      <Seo title="Amministrazione" />
      <PageTitle title="Amministrazione" />

      <div className="idm-container idm-admin-layout">
        <div className="idm-admin-toolbar">
          <span data-testid="admin-count">{tab === "portfolio" ? `${items.length} lavori` : ""}</span>
          <button className="idm-admin-logout" onClick={handleLogout} data-testid="admin-logout">
            <LogOut size={14} /> Esci
          </button>
        </div>

        {/* Sections menu */}
        <nav className="idm-admin-tabs" data-testid="admin-tabs">
          {ADMIN_TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "active" : ""}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </nav>

        {tab === "portfolio" && (
          <>
            {/* Add / Edit form */}
            <form className="idm-admin-form" onSubmit={handleSave} data-testid="admin-form">
              <h3>
                {editingId ? <><Pencil size={16} /> Modifica lavoro</> : <><Plus size={16} /> Aggiungi nuovo lavoro</>}
                {editingId && (
                  <button type="button" className="idm-admin-cancel" onClick={cancelEdit} data-testid="admin-cancel-edit">
                    <X size={14} /> Annulla
                  </button>
                )}
              </h3>
              <div className="idm-form-grid">
                <div className="idm-field half">
                  <label>Titolo (IT) *</label>
                  <input value={form.title_it} onChange={(e) => setForm({ ...form, title_it: e.target.value })} data-testid="admin-title-it" />
                </div>
                <div className="idm-field half">
                  <label>Titolo (EN)</label>
                  <input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="admin-title-en" />
                </div>
                <div className="idm-field half">
                  <label>Categoria</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-category" className="idm-select">
                    {cats.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name[lang]}</option>
                    ))}
                  </select>
                </div>
                <div className="idm-field half">
                  <label>Immagine</label>
                  <div className="idm-admin-imgrow">
                    <label className="idm-upload-btn">
                      {uploading ? <Loader2 size={14} className="idm-spin" /> : <Upload size={14} />}
                      {uploading ? " Caricamento..." : " Carica file"}
                      <input type="file" accept="image/*" onChange={handleUpload} hidden data-testid="admin-upload" />
                    </label>
                    {form.image && <img src={imgSrc(form.image)} alt="" className="idm-admin-thumb" />}
                  </div>
                </div>
                <div className="idm-field half">
                  <label>Descrizione (IT)</label>
                  <textarea rows={2} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })} data-testid="admin-desc-it" />
                </div>
                <div className="idm-field half">
                  <label>Descrizione (EN)</label>
                  <textarea rows={2} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="admin-desc-en" />
                </div>

                {/* Project sheet (dark panel data) */}
                <div className="idm-admin-sheet">
                  <p className="idm-admin-sheet-title">Scheda progetto (pannello scuro)</p>
                  <div className="idm-form-grid">
                    <div className="idm-field half">
                      <label>Tipologia sito (IT)</label>
                      <input value={form.tipologia} onChange={(e) => setForm({ ...form, tipologia: e.target.value })} placeholder="es. E-commerce" data-testid="admin-meta-tipologia" />
                    </div>
                    <div className="idm-field half">
                      <label>Tipologia sito (EN)</label>
                      <input value={form.tipologia_en} onChange={(e) => setForm({ ...form, tipologia_en: e.target.value })} placeholder="e.g. E-commerce shop" />
                    </div>
                    <div className="idm-field half">
                      <label>Data (IT)</label>
                      <input value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} placeholder="es. 12 Luglio, 2024" data-testid="admin-meta-data" />
                    </div>
                    <div className="idm-field half">
                      <label>Data (EN)</label>
                      <input value={form.data_en} onChange={(e) => setForm({ ...form, data_en: e.target.value })} placeholder="e.g. July 12, 2024" />
                    </div>
                    <div className="idm-field half">
                      <label>SEO (IT)</label>
                      <input value={form.seo} onChange={(e) => setForm({ ...form, seo: e.target.value })} placeholder="es. BASIC / MEDIUM / PRO" data-testid="admin-meta-seo" />
                    </div>
                    <div className="idm-field half">
                      <label>SEO (EN)</label>
                      <input value={form.seo_en} onChange={(e) => setForm({ ...form, seo_en: e.target.value })} placeholder="e.g. BASIC / MEDIUM / PRO" />
                    </div>
                    <div className="idm-field half">
                      <label>Cliente</label>
                      <input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} data-testid="admin-meta-cliente" />
                    </div>
                    <div className="idm-field half">
                      <label>URL</label>
                      <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="es. www.cliente.com" data-testid="admin-meta-url" />
                    </div>
                    <div className="idm-field half">
                      <label>Sede legale (IT)</label>
                      <input value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })} data-testid="admin-meta-sede" />
                    </div>
                    <div className="idm-field half">
                      <label>Sede legale (EN)</label>
                      <input value={form.sede_en} onChange={(e) => setForm({ ...form, sede_en: e.target.value })} placeholder="e.g. Milan, Italy" />
                    </div>
                    {SECTION_DEFS.map((def) => (
                      <React.Fragment key={def.key}>
                        <div className="idm-field half">
                          <label>{def.title} (IT) — una voce per riga</label>
                          <textarea
                            rows={3}
                            value={form[def.key]}
                            onChange={(e) => setForm({ ...form, [def.key]: e.target.value })}
                            data-testid={`admin-${def.key}`}
                          />
                        </div>
                        <div className="idm-field half">
                          <label>{def.title} (EN) — one per line</label>
                          <textarea
                            rows={3}
                            value={form[def.keyEn]}
                            onChange={(e) => setForm({ ...form, [def.keyEn]: e.target.value })}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="idm-btn-yellow"
                  style={{ animation: "none", background: "var(--idm-dark, #1a1a2e)", color: "#fff" }}
                  disabled={translating}
                  onClick={handleAutoTranslate}
                  data-testid="admin-auto-translate"
                >
                  {translating ? <Loader2 size={16} className="idm-spin" /> : <Languages size={16} />}
                  {translating ? " Traduzione..." : " Auto-traduci IT→EN"}
                </button>
                <button type="submit" className="idm-btn-yellow" style={{ animation: "none" }} disabled={saving} data-testid="admin-save">
                  {saving ? <Loader2 size={16} className="idm-spin" /> : editingId ? "Salva modifiche" : "Aggiungi lavoro"}
                </button>
              </div>
            </form>

            {/* Items list */}
            {loading ? (
              <div className="idm-loading"><span className="idm-spinner" /></div>
            ) : (
              <div className="idm-admin-list" data-testid="admin-list">
                {items.map((item) => (
                  <div className="idm-admin-row" key={item.id} data-testid={`admin-row-${item.id}`}>
                    {item.image ? (
                      <img src={imgSrc(item.image)} alt="" loading="lazy" />
                    ) : (
                      <div className="idm-admin-noimg" />
                    )}
                    <div className="idm-admin-row-info">
                      <strong>{item.title.it}</strong>
                      <span>{item.category}</span>
                    </div>
                    <div className="idm-admin-row-actions">
                      <button onClick={() => startEdit(item)} aria-label="Modifica" data-testid={`admin-edit-${item.id}`}>
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => handleDelete(item)} className="danger" aria-label="Elimina" data-testid={`admin-delete-${item.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "ordini" && <OrdersAdmin authHeaders={authHeaders} />}

        {tab === "clienti" && <CustomersAdmin authHeaders={authHeaders} />}

        {tab === "prodotti" && <ProductsAdmin authHeaders={authHeaders} />}

        {tab === "file" && <ProductFilesAdmin authHeaders={authHeaders} />}

        {tab === "licenze" && <LicensesAdmin authHeaders={authHeaders} />}

        {tab === "pagine" && <LegalPagesAdmin authHeaders={authHeaders} />}

        {tab === "impostazioni" && <SettingsAdmin authHeaders={authHeaders} />}
      </div>
    </div>
  );
};

export default AdminPage;
